import { initSmokeballSdkStore } from './use-smokeball-sdk-store'
import { initFeatureFlagsStore } from './use-feature-flags-store'
import { initIngestionTrackingStore } from './use-ingestion-tracking-store';
import { initMatterEntityDetailsAdminStore } from './use-matter-entity-details-admin-store';
import { initMatterAvailableKnowledgeSourcesStore } from './use-matter-available-knowledge-sources-store';
import { initMatterTypesStore } from './use-matter-types-store';
import { initModelInfoStore } from './use-model-info-store';
import { initTelemetryPanelStore } from './use-telemetry-panel-store';
import { initAccountMatterStore } from './use-account-matter-store';
import { initChatManagerStore } from './use-chat-manager-store';
import { useAdminStore } from './use-admin-store';

type UnsubFunc = () => (void | Promise<void>);
type UnsubFuncReturnType = void | ((UnsubFunc)[]);
type StoreInitFunc = () => UnsubFuncReturnType | Promise<UnsubFuncReturnType>;

/*
 * Some stores may depend on other stores to keep their data up to date.
 *
 * If they set up subscriptions, this could happen after data in the store
 * subscribed to has already changed so it is important to do a fetch in
 * the init methods.
 *
 * The order of setting up the stores should not matter (or rather the init
 * functions should be written in a way that it does not matter).
 *
 */

export const initClientStores = async () => {
  if (window === undefined) {
    return;
  }
  const unsubs: UnsubFunc[] = [];

  const initStore = async (initFunc: StoreInitFunc) => {
    const result = await initFunc();
    if (result && Array.isArray(result) && result.length) {
      unsubs.push(...result);
    }
  }

  await initStore(initFeatureFlagsStore);
  await initStore(initIngestionTrackingStore);
  await initStore(initSmokeballSdkStore);
  
  // Only initialize admin store for admin users
  const { isAdminUser } = useAdminStore.getState();
  if (isAdminUser) {
    await initStore(initMatterEntityDetailsAdminStore);
  }
  
  await initStore(initMatterTypesStore);
  await initStore(initModelInfoStore);
  await initStore(initTelemetryPanelStore);
  await initStore(initAccountMatterStore);
  await initStore(initChatManagerStore);
  await initStore(initMatterAvailableKnowledgeSourcesStore);

  return async () => Promise.allSettled(unsubs.map((u) => u()))
}
